import React, { useEffect, useRef } from 'react';
import { ArrowLeft, CheckCircle, Phone, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { businessInfo } from '../mock';
import { useSale } from '../context/SaleContext';
import { trackMetaEvent } from '../lib/meta';

const ThankYou = () => {
  const navigate = useNavigate();
  const { decrementSlot } = useSale();
  const hasDecremented = useRef(false);
  const hasTracked = useRef(false);

  // Track Schedule and Purchase events when user lands on thank-you page
  // Also decrement the sale slot counter
  useEffect(() => {
    // Decrement slot for unique visitor (only once)
    if (!hasDecremented.current) {
      hasDecremented.current = true;
      decrementSlot();
    }

    // Fire Meta events via both Pixel + Conversions API (server-side)
    // with a shared event ID so Meta can deduplicate them.
    if (!hasTracked.current) {
      hasTracked.current = true;
      trackMetaEvent({
        eventName: 'Schedule',
        customData: {
          content_name: 'Mobile Car Detailing Booking',
          content_category: 'Mobile Car Detailing',
        },
      });
      trackMetaEvent({
        eventName: 'Purchase',
        customData: {
          value: 299.00, // Average service value for ROAS calculation
          currency: 'CAD',
          content_type: 'product',
          content_name: 'Mobile Car Detailing Service',
          content_category: 'Mobile Car Detailing',
        },
      });
    }
  }, []);

  const handleBackHome = () => {
    navigate('/');
  };

  const handleCall = () => {
    window.location.href = `tel:${businessInfo.phone}`;
  };

  return (
    <section className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Same Background as Hero */}
      <div className="absolute inset-0 w-full h-full">
        <img 
          src="https://images.unsplash.com/photo-1629281066736-ff3a1e6b36d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBkZXRhaWxpbmd8ZW58MHx8fHwxNzU4NjYxMzA3fDA&ixlib=rb-4.1.0&q=85"
          alt="Luxury car detailing"
          className="w-full h-full object-cover"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
      </div>

      {/* Thank You Content */}
      <div className="container relative z-10 text-center">
        <div className="max-w-2xl mx-auto">
          {/* Success Icon */}
          <CheckCircle size={80} className="text-green-400 mx-auto mb-8" />
          
          <h1 className="brand-display text-white mb-6">
            Thank You!
          </h1>
          
          <p className="body-large text-gray-200 mb-8">
            Your booking request has been submitted successfully. 
          </p>
          
          <p className="body-medium text-gray-300 mb-12">
            We'll contact you shortly to confirm your appointment details. 
            Our premium mobile detailing service will give your vehicle that "new car feel"!
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12 justify-center">
            <button 
              onClick={handleBackHome}
              className="btn-primary flex items-center space-x-2"
            >
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </button>
            
            <button 
              onClick={handleCall}
              className="btn-secondary flex items-center space-x-2"
            >
              <Phone size={16} />
              <span>Call Us: {businessInfo.phone}</span>
            </button>
          </div>

          {/* Additional Info */}
          <div className="bg-black/40 backdrop-blur-sm rounded-lg p-6">
            <h3 className="heading-3 text-lg text-white mb-4">What Happens Next?</h3>
            <div className="space-y-3 text-left max-w-md mx-auto">
              <div className="flex items-start space-x-3">
                <Calendar size={16} className="text-blue-400 mt-1 flex-shrink-0" />
                <span className="body-small text-gray-300">We'll confirm your appointment time (8AM or 1PM slots available)</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle size={16} className="text-blue-400 mt-1 flex-shrink-0" />
                <span className="body-small text-gray-300">Our team will arrive at your location with professional equipment</span>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle size={16} className="text-blue-400 mt-1 flex-shrink-0" />
                <span className="body-small text-gray-300">Enjoy your freshly detailed vehicle with 6-12 month ceramic wax coating</span>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="mt-8 pt-8 border-t border-gray-600">
            <p className="body-small text-gray-400">
              Questions? Contact us at <strong className="text-white">{businessInfo.phone}</strong> or <strong className="text-white">{businessInfo.email}</strong>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ThankYou;